import {
    useEffect, useMemo, useRef, useState,
} from 'react';
import { InternalOverlay, RenderIf } from 'react-rainbow-components';
import { isEmpty } from '@rainbow-modules/validation';
import { query, where } from 'firebase/firestore';
import { useOutsideClick, useWindowResize } from '@rainbow-modules/hooks';
import useArrowScrolling from 'components/FrequentMessage/hooks/useArrowScrolling';
import Clipboard from 'components/icons/clipboard';
import SearchIcon from 'components/icons/search';
import ChevronUp from 'components/icons/chevronUp';
import ChevronDown from 'components/icons/chevronDown';
import useLLmTestInputs from 'data/firestore/agent/llmtestinput/useCollection';
import { Llmtestinput } from 'data/firestore/agent/llmtestinput/types';
import { ActionButton } from '../styled';
import {
    BottomGradient,
    Dropdown,
    DropdownContent,
    DropdownHeader,
    DropdownTitle,
    ListContainer,
    SearchInput,
    StyledEmptyMessage,
    StyledUl,
    TopGradient,
} from './styled';
import { LLMTestInputPickerButtonProps } from './types';
import InputsList from './inputsList';

const LLMTestInputPickerButton = ({
    agentId,
    featureSchema,
    onSelect = () => {},
    onDelete = () => {},
}: LLMTestInputPickerButtonProps) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isOverlayVisible, setOverlayVisible] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const { data } = useLLmTestInputs(agentId, {
        listQueryFn: (collectionRef) => {
            if (featureSchema) {
                return query(collectionRef, where('featureSchema', '==', featureSchema));
            }

            return collectionRef;
        },
    });

    const {
        showScrollUpArrow,
        showScrollDownArrow,
        setRef: menuRef,
        scrollUp,
        scrollDown,
        stopScolling,
        updateScrollingArrows,
    } = useArrowScrolling<HTMLUListElement>(isOverlayVisible);

    useOutsideClick(
        dropdownRef,
        (event) => {
            // eslint-disable-next-line max-len
            if (event.target !== triggerRef.current) {
                setOverlayVisible(false);
            }
        },
        isOverlayVisible,
    );

    useWindowResize(
        () => setOverlayVisible(false),
        isOverlayVisible,
    );

    const handleSelect = (llmTestInput: Llmtestinput) => {
        onSelect(llmTestInput);
        setOverlayVisible(false);
    };

    const handleRequestDelete = (id: string) => {
        onDelete(id);
        setOverlayVisible(false);
    };

    const isEmptyList = data.length === 0;

    const filteredData = useMemo(
        () => {
            const regex = new RegExp(searchQuery, 'gi');
            return (
                isEmpty(searchQuery)
                    ? data
                    : data.filter(
                        (message) => regex.test(message.title) || regex.test(message.content),
                    )
            );
        },
        [data, searchQuery],
    );

    useEffect(() => {
        if (isOverlayVisible) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);
        }
    }, [isOverlayVisible]);

    return (
        <>
            <ActionButton
                tooltip="Use previously stored inputs"
                icon={<Clipboard title=" " />}
                onClick={() => setOverlayVisible(!isOverlayVisible)}
                borderRadius="semi-square"
                ref={triggerRef}
            />
            <InternalOverlay
                isVisible={isOverlayVisible}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                triggerElementRef={() => triggerRef?.current?.buttonRef}
            >
                <Dropdown ref={dropdownRef}>
                    <DropdownHeader>
                        <DropdownTitle>Saved LLM Testing inputs</DropdownTitle>
                    </DropdownHeader>
                    <RenderIf isTrue={!isEmptyList}>
                        <DropdownContent>
                            <SearchInput
                                icon={<SearchIcon />}
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                ref={searchInputRef}
                            />
                            <ListContainer>
                                <RenderIf isTrue={showScrollUpArrow}>
                                    <TopGradient
                                        onMouseEnter={scrollUp}
                                        onMouseLeave={stopScolling}
                                    >
                                        <ChevronUp />
                                    </TopGradient>
                                </RenderIf>
                                <StyledUl
                                    ref={menuRef}
                                    role="presentation"
                                    onScroll={updateScrollingArrows}
                                >
                                    <InputsList
                                        data={filteredData}
                                        onInputClick={handleSelect}
                                        onRequestDelete={handleRequestDelete}
                                    />
                                </StyledUl>
                                <RenderIf
                                    isTrue={showScrollDownArrow && filteredData.length > 5}
                                >
                                    <BottomGradient
                                        onMouseEnter={scrollDown}
                                        onMouseLeave={stopScolling}
                                    >
                                        <ChevronDown />
                                    </BottomGradient>
                                </RenderIf>
                            </ListContainer>
                        </DropdownContent>
                    </RenderIf>
                    <RenderIf isTrue={isEmptyList}>
                        <StyledEmptyMessage
                            message="Save inputs here to quickly insert them when testing AI Persona."
                            title="No inputs have been saved"
                            size="small"
                        />
                    </RenderIf>
                </Dropdown>
            </InternalOverlay>
        </>
    );
};

export default LLMTestInputPickerButton;
